import { Card, SkyClubTracker } from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  AEMErrorMessage,
  UserSession,
  checkErrors,
  formatLongMonthDate,
  getEmptyContent,
  getImage,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { logError } from '@com-delta-idp/mnx-utils';
import { SkyClub, SkyClubResponse } from '../models';
import {
  getImageData,
  getSkyClubDescription,
  getUnlimited,
  getVisitsObject,
  sortedData
} from '../utils/skyclub-tracker.utils';

export const getSkyClubTracker = (
  aem: SkyClub | undefined,
  skyClub: SkyClubResponse | undefined,
  profile: UserSession | undefined,
  featureFlag: boolean | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): SkyClubTracker => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<SkyClubTracker>(
        'skyclub-tracker',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      SkyClub,
      [
        SkyClub | undefined,
        UserSession | undefined,
        SkyClubResponse | undefined,
        boolean | undefined
      ]
    >('title', aem, profile, skyClub, featureFlag)
      ? adaptSkyClubTrackerObject(
          aem as SkyClub,
          skyClub as SkyClubResponse,
          profile as UserSession,
          featureFlag as boolean
        )
      : isDisplay<SkyClubTracker>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'SKYCLUB_TRACKER');
    return getEmptyContent<SkyClubTracker>(
      'skyclub-tracker',
      aemErrorMessages,
      true
    );
  }
};

export const adaptSkyClubTrackerObject = (
  aem: SkyClub,
  skyClub: SkyClubResponse,
  profile: UserSession,
  featureFlag: boolean
): SkyClubTracker => {
  if (featureFlag && skyClub.visitRecord.length) {
    return {
      header: aem.title,
      cards: getSkyClubCards(skyClub, profile, aem),
      disabled: !featureFlag,
      loaded: true
    };
  } else {
    return getEmptyContent<SkyClubTracker>('', [], true, true);
  }
};

const getSkyClubCards = (
  skyClub: SkyClubResponse,
  profile: UserSession,
  aem: SkyClub
): Card[] => {
  const cards: Card[] = [];
  skyClub.visitRecord.forEach((visitRecord) => {
    const result: Card = {
      name:
        aem.cardTile.find(
          (tile) => tile.type === visitRecord.affiliationTypeCode
        )?.title || visitRecord.affiliationTypeDesc,
      description: getSkyClubDescription(aem, visitRecord),
      image: getImageData(aem.cardTile, visitRecord.affiliationTypeCode),
      visits: getVisitsObject(aem, visitRecord),
      unlimited: getUnlimited(aem, visitRecord)
    };
    cards.push(result);
  });
  if (profile.isSkyClubMember === 'Y') {
    cards.push({
      name: aem.unlimited.membershipTitle,
      description: '',
      unlimited: {
        unlocked: true, //TODO(KAI,Nelson): Resolve this issue as unlocked value should be false in case of skyClub membership
        membership: true,
        image: getImage(aem.unlimited.imgSrc, aem.unlimited.imgAlt),
        title: aem.unlimited.membershipDesc,
        subtitle:
          aem.unlimited.membershipValidity +
          ' ' +
          (profile.skyClubMembershipDiscontinueDate &&
            formatLongMonthDate(profile.skyClubMembershipDiscontinueDate))
      }
    });
  }

  return sortedData(cards);
};
